import * as React from 'react';

import { RouteStore } from 'bernie-core';

import { AlternateDatesModel, HotelViewModel } from 'models';

import { UitkCard, UitkCardContent, UitkCardImageLayout } from 'uitk-react-cards';

import { AdditionalInfo } from './additional-info';
import { AlternateDates } from './alternate-dates';
import { BadgeGenerator } from 'components/common/badge-generation';
import { CardLink } from './card-link';
import { HighightedPropertyDetails } from './highlighted-property-details';
import { ListingCommonSection } from './listing-common-section';
import { ListingErrorBoundary } from './listing-error-boundary';
import { ListingFooter } from './footer';
import { ListingName } from './listing-name';
import { ListingPropertyBadges } from './listing-property-badges';
import { ListingTravelAd } from './listing-travel-ad';
import { Neighborhood } from './neighborhood';
import { PriceAndSupportingMessagesContainer } from './price-and-supporting-messages-container';
import { PrimaryBadge } from './primary-badge';
import { SearchListingPlaceholderLarge } from 'src/common/components/infosite/rooms-and-rates/loading-placeholder';
import { Stars } from './star-rating';
import { TitleHeading } from './title-heading';

interface HorizontalListingProps {
  className: string;
  index?: number;
  loading?: boolean;
  model: HotelViewModel;
  routeStore?: RouteStore;
  isDatelessPdp?: boolean;
  defaultListingTopMarginClass?: string;
  stars?: number;
  alternateDatesProps?: {
    handleClick: () => void;
    model: AlternateDatesModel[];
  };
  // eslint-disable-next-line no-unused-vars
  getTitleComponent: (imageAspectRatio?: string, extraProps?: any) => any;
  imageAspectRatio?: string;
  alternateDatesInfo: {
    collapsedLinkName: string;
    collapsedMsgKey: string;
    collapsedRfrr: string;
    expandedLinkName: string;
    expandedMsgKey: string;
    expandedRfrr: string;
    headerTextKey: string;
  };
  dataStid?: string;
  cardLinkProps?: any;
  routeStoreGoBack?: () => void;
  listingClickHandler?: () => void;
  setItemRef?: () => void;
  mapListingView?: boolean;
}

export const HorizontalListing: React.FunctionComponent<HorizontalListingProps> = ({
  alternateDatesProps,
  index,
  loading,
  model,
  routeStore,
  isDatelessPdp,
  className,
  stars,
  getTitleComponent,
  imageAspectRatio,
  alternateDatesInfo,
  dataStid,
  cardLinkProps,
  routeStoreGoBack,
  listingClickHandler,
  setItemRef,
  mapListingView,
}) => {
  const {
    alternateDates,
    listingFooter,
    name,
    neighborhood,
    pinnedDetails,
    price,
    propertyListingBadgeModel,
    propertySummary,
    secondaryCardMessage,
    supportingMessages,
    thumbnail,
    travelAd,
    recentlyViewed,
    propertyDetailsLink,
  } = model;
  const listingWrapperProps = {
    className,
    'data-stid': dataStid || '',
    ref: setItemRef,
    tabIndex: -1,
  };
  listingWrapperProps.className += ' horizontal';

  const highlightedPropertyDetailsProps = {
    propertyDetails: propertySummary,
  };

  const secondary = propertyListingBadgeModel && propertyListingBadgeModel.secondary;
  const tertiary = propertyListingBadgeModel && propertyListingBadgeModel.tertiary;
  const pinned = Boolean(pinnedDetails?.heading);

  const horizontalPriceAndSupportingMessagesContainerProps = {
    ...{ secondaryCardMessage, supportingMessages, price, secondary },
    infositeUrl: propertyDetailsLink?.uri?.relativePath,
    viewSmall: false,
    wrapPrice: false,
    priceFontWeight: '600',
    hotelId: model.hotelId,
    recentlyViewed,
    isDatelessPdp,
    index,
  };

  if (loading) {
    return (
      <li {...listingWrapperProps}>
        <SearchListingPlaceholderLarge />
      </li>
    );
  }

  return (
    <li {...listingWrapperProps}>
      <ListingName visuallyHidden={true} />
      <UitkCard imageLayout={UitkCardImageLayout.LEFT_FULLBLEED}>
        {!mapListingView && pinned && <TitleHeading data-stid="title-heading" headingInfo={pinnedDetails} />}
        <ListingErrorBoundary thumbnail={thumbnail} name={name}>
          {getTitleComponent(imageAspectRatio)}
        </ListingErrorBoundary>
        <PrimaryBadge propertyListingBadgeModel={propertyListingBadgeModel} travelAd={travelAd} pinned={pinned} />
        <UitkCardContent className="all-y-padding-three all-x-padding-three listing-content">
          <>
            <div className="uitk-grid all-cell-fill all-cell-align-top">
              <ListingPropertyBadges />
              <ListingName lineCount={2} />
              <div className="uitk-grid all-cell-fill">
                <div className="uitk-cell all-cell-2-3 uitk-type-300">
                  <Stars stars={stars} />
                  <HighightedPropertyDetails {...highlightedPropertyDetailsProps} />
                  <Neighborhood neighborhood={neighborhood} />
                </div>
              </div>
            </div>
            <div className="uitk-cell all-cell-align-bottom">
              <div className="uitk-grid">
                <div className="uitk-cell all-cell-fill all-cell-align-bottom">
                  <ListingTravelAd />
                  <ListingCommonSection model={model} routeStore={routeStore} routeStoreGoBack={routeStoreGoBack} />
                </div>
                <div className="uitk-cell all-cell-shrink all-x-gutter-two all-cell-align-bottom uitk-type-right">
                  <PriceAndSupportingMessagesContainer {...horizontalPriceAndSupportingMessagesContainerProps} />
                  {tertiary && tertiary.text && (
                    <div className="uitk-cell all-x-gutter-two uitk-type-right all-cell-shrink all-t-padding-half">
                      {!isDatelessPdp && <BadgeGenerator badge={tertiary} />}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
          <CardLink {...cardLinkProps} index={index} listingClickHandler={listingClickHandler} />
        </UitkCardContent>
        {!mapListingView && (
          <ListingFooter
            {...cardLinkProps}
            data-stid="listing-footer"
            listingFooter={listingFooter}
            listingClickHandler={listingClickHandler}
          />
        )}
        {!mapListingView && alternateDates && (
          <AdditionalInfo {...alternateDatesInfo} displayHeaderText={true} keepOpen={true}>
            <AlternateDates data-stid="alternate-dates" {...alternateDatesProps} />
          </AdditionalInfo>
        )}
      </UitkCard>
    </li>
  );
};
